<template>
  <div class="talking-point-resource-form-ui__message">
    <VFormFeedback :class="formFeedbackVariationClassName" :error="errors.text && errors.text[0]">
      <VInput
        v-model="payload.text"
        :config="{ label: 'Talking point text', required: true }"
        class="slim"
      />
    </VFormFeedback>
  </div>
  <div class="talking-point-resource-form-ui__dates">
    <VFormFeedback
      :class="formFeedbackVariationClassName"
      :error="
        (errors.start_date && errors.start_date[0]) || (errors.end_date && errors.end_date[0])
      "
    >
      <DatePickerRange
        :select="payload"
        :variant="datePickerVariant"
        :position="datePickerPosition"
        :auto-position="datePickerAutoPosition"
        :allow-open-end="true"
        @update="setFilters"
      />
    </VFormFeedback>
  </div>
  <div class="talking-point-resource-form-ui__patches">
    <VFormFeedback
      :class="formFeedbackVariationClassName"
      :error="errors.patches && errors.patches[0]"
    >
      <PatchInput
        v-model="payload.patches"
        :data-client="patchDataClient"
        label="Patches"
        variant-class-name="slim panel-border-emphasis"
      />
    </VFormFeedback>
  </div>
  <div class="talking-point-resource-form-ui__submit">
    <VBtn class="full-width slim" type="button" :loading="isLoading" @click="onSubmit">{{
      btnText
    }}</VBtn>
  </div>
</template>

<script setup lang="ts">
  // Types
  import type { PropType } from 'vue';
  import type {
    TErrors,
    TNewTalkingPoint,
    TTalkingPoint,
    ITalkingPointsResourceModel,
  } from '~/misc/talking-points-resource/types';
  import type { TPatchDataClient } from '~/misc/patch/types';
  import type { TDateRange } from '~/misc/date-picker-range/types';

  // Composables
  import { useUpsertResource } from '~/misc/resource/composables/use-upsert-resource';

  // Components
  import VFormFeedback from '~/misc/v-form-feedback';
  import VInput from '~/misc/v-input';
  import VBtn from '~/misc/v-btn';
  import PatchInput from '~/misc/patch/input/PatchInput.vue';
  import DatePickerRange from '~/misc/date-picker-range/DatePickerRange.vue';

  const props = defineProps({
    model: {
      type: Object as PropType<ITalkingPointsResourceModel>,
      required: true,
    },
    patchDataClient: {
      type: Object as PropType<TPatchDataClient>,
      required: true,
    },
    talkingPoint: {
      type: Object as PropType<TNewTalkingPoint | null>,
      default: null,
    },
    formFeedbackVariationClassName: {
      type: String,
      default: '',
    },
    btnText: {
      type: String,
      default: 'Add',
    },
    fullControl: {
      type: Boolean,
      default: false,
    },
    datePickerVariant: {
      type: String as PropType<'default' | 'compact'>,
      default: 'default',
    },
    datePickerPosition: {
      type: String as PropType<'left' | 'center' | 'right'>,
      default: 'center',
    },
    datePickerAutoPosition: {
      type: [Boolean, String] as PropType<boolean | 'top' | 'bottom'>,
      default: false,
    },
  });

  const { payload, errors, isLoading, submit } = useUpsertResource<
    TNewTalkingPoint,
    TErrors,
    TTalkingPoint
  >(
    props.model,
    {
      text: '',
      start_date: '',
      end_date: '',
      patches: [],
    },
    props.talkingPoint
  );

  const emit = defineEmits<{
    (e: 'upsert', talkingPoint: TTalkingPoint): void;
  }>();

  const onSubmit = () => {
    submit()
      .then((talkingPoint: TTalkingPoint) => {
        emit('upsert', talkingPoint);
      })
      .catch(() => {
        // suppress
      });
  };

  const setFilters = (filters: TDateRange) => {
    payload.value.start_date = filters.startDate ?? '';
    payload.value.end_date = filters.endDate ?? '';
  };
</script>
